import * as React from 'react'

import apis from '@app/api/api'
import useI18n from '@store/i18n/useI18n'
import Logger from '@utils/Logger'
import theme from '@theme/Theme'
import { motion, container } from '@utils/Motion'

import styled from '@theme/styled-components'
import Searchbar from '@components/searchbar/searchbar'
import Button from '@components/button/button'
import Loader from 'react-loader-spinner'
import TextInput from '@components/textInput/textInput'
import MultiSelect from '@components/select/multiSelect'
import Select from '@components/select/select'

import Swal from 'sweetalert2'
import ScreenContainer from '@components/screenContainer/screenContainer'
import InfiniteList from '@components/infiniteList/infiniteList'
import AdminBusinesses from '@components/listItem/adminBusinesses'
import useDebounce from '@utils/useDebounce'

interface Props {
  navigation: Navigation
}

const adminBusinesses = ({ navigation }: Props) => {
  const i18n = useI18n()

  const [screenStatus, setScreenStatus] = React.useState<'fetched' | 'loading' | 'error'>('loading')

  const [text, bounced, setText] = useDebounce('')
  const [regionsOptions, setRegionsOptions] = React.useState<Option[]>([])
  const [regionSelected, setRegionSelected] = React.useState<Option | undefined>()

  const [adminBusinesses, setAdminBusinesses] = React.useState<AdminBusiness[]>([])

  const [selectedBusinessId, setSelectedBusinessId] = React.useState<string | undefined>()
  const [selectedBusiness, setSelectedBusiness] = React.useState<AdminBusiness | undefined>(undefined)
  const [companyList, setCompanyList] = React.useState<Option[]>([])
  const [dirtyForm, setDirtyForm] = React.useState<boolean>(false)

  React.useEffect(() => {
    initialFetch()
  }, [])

  React.useEffect(() => {
    if (regionSelected) {
      fetchBusinesses(regionSelected.value)
    }
  }, [regionSelected, bounced])

  React.useEffect(() => {
    if (selectedBusinessId) {
      apis.admin.business
        .list({ id: selectedBusinessId })
        .then((res) => {
          setSelectedBusiness(res.businesses[0])
        })
        .catch((err) => {
          setScreenStatus('error')
          Logger.error("Une erreur est survenue à la récuperation de l'affaire : ", err)
        })
    } else {
      setSelectedBusiness(undefined)
    }
  }, [selectedBusinessId])

  const initialFetch = () =>
    Promise.all([apis.admin.list.regions(), apis.admin.list.companies()])
      .then(([fetchedRegions, fetchedCompanies]) => {
        const initialRegionOption = fetchedRegions.regions[0]
        setRegionSelected({ label: initialRegionOption.libelle, value: initialRegionOption.id })
        setRegionsOptions(
          fetchedRegions.regions.map((opt) => {
            return { label: opt.libelle, value: opt.id }
          })
        )
        setCompanyList(
          fetchedCompanies.companies.map((opt) => {
            return { label: opt.libelle, value: opt.id }
          })
        )
      })
      .catch((err) => {
        Logger.info('ERROR FETCHING ADMIN BUSINESSES DATA : ', err)
        setScreenStatus('error')
      })

  const fetchBusinesses = (region: string) => {
    setScreenStatus('loading')
    apis.admin.business
      .list({ text, region })
      .then((fetchedBusinesses) => {
        setAdminBusinesses(fetchedBusinesses.businesses.sort((a, b) => a.title.localeCompare(b.title)))
        setScreenStatus('fetched')
      })
      .catch((err) => {
        Logger.info('ERROR FETCHING ADMIN BUSINESSES : ', err)
        setScreenStatus('error')
      })
  }

  const setFormField = (callback: () => void) => {
    setDirtyForm(true)
    callback()
  }

  const alertCancellingEdition = (callback?: () => void) => {
    if (dirtyForm) {
      Swal.fire({
        title: i18n.t('components.adminBusinesses.alertCancelling.title'),
        text: i18n.t('components.adminBusinesses.alertCancelling.text'),
        showCancelButton: true,
        confirmButtonText: i18n.t('components.adminBusinesses.alertCancelling.confirm'),
        confirmButtonColor: 'green',
        cancelButtonText: i18n.t('components.adminBusinesses.alertCancelling.cancel'),
        cancelButtonColor: 'red',
      }).then((result) => {
        if (result.isConfirmed) {
          setSelectedBusinessId(undefined)
          setDirtyForm(false)
          !!callback && callback()
        }
      })
    } else {
      setSelectedBusinessId(undefined)
      setDirtyForm(false)
      !!callback && callback()
    }
  }

  const saveBusiness = (business: AdminBusiness) => {
    apis.admin.business
      .edit(business)
      .then((b: AdminBusiness) => {
        Swal.fire({
          title: i18n.t(`components.adminBusinesses.alertSaving.success.title`),
          text: i18n.t(`components.adminBusinesses.alertSaving.success.text`),
          icon: 'success',
        })
        setAdminBusinesses(
          [...adminBusinesses.filter((bu) => bu.id !== b.id), b].sort((a, b) => a.title.localeCompare(b.title))
        )
        setSelectedBusinessId(undefined)
        setDirtyForm(false)
      })
      .catch((err) => {
        Swal.fire({
          title: i18n.t(`components.adminBusinesses.alertSaving.failure.title`),
          text: i18n.t(`components.adminBusinesses.alertSaving.failure.text`),
          icon: 'error',
        })
        Logger.info('ERROR SAVING BUSINESS : ', err)
      })
  }

  const valideBusiness = React.useMemo(() => {
    return !!selectedBusiness && !!selectedBusiness.title && !!selectedBusiness.subtitle && !!selectedBusiness.address
  }, [selectedBusiness])

  const formErrors = React.useMemo(
    () =>
      selectedBusiness && {
        title: !selectedBusiness.title ? i18n.t('components.adminBusinesses.errors.title') : undefined,
        subtitle: !selectedBusiness.subtitle ? i18n.t('components.adminBusinesses.errors.subtitle') : undefined,
        address: !selectedBusiness.address ? i18n.t('components.adminBusinesses.errors.address') : undefined,
      },
    [selectedBusiness]
  )

  return (
    <ScreenContainer
      menu={
        <LeftContainer>
          <ScreenTitleContainer>
            <ScreenTitle>{i18n.t('components.adminBusinesses.screenTitle')}</ScreenTitle>
            <Back onClick={() => navigation.push('/')}>{i18n.t('components.menu.back')}</Back>
          </ScreenTitleContainer>
          <Searchbar placeholder={i18n.t('components.adminBusinesses.searchbar')} text={text} setText={setText} />
          {regionsOptions && (
            <SelectContainer>
              <Select
                options={regionsOptions}
                selected={regionSelected}
                setSelected={(r) => setRegionSelected(r)}
                placeholder={i18n.t('components.menu.placeholder.regions')}
                isClearable={false}
                isDisabled={screenStatus === 'loading'}
                noMargin
              />
            </SelectContainer>
          )}
          {screenStatus === 'loading' ? (
            <StatusContainer>
              <Loader type="ThreeDots" color={theme.colors.classicButtonLoader} height={30} />
            </StatusContainer>
          ) : screenStatus === 'error' ? (
            <ErrorLibelle>{i18n.t('components.adminBusinesses.error')}</ErrorLibelle>
          ) : (
            <BusinessList as={motion.div} className="container" variants={container} initial="hidden" animate="visible">
              {adminBusinesses.length === 0 ? (
                <NoItemContainer>
                  <NoItemLibelle>{i18n.t('components.adminBusinesses.noItem')}</NoItemLibelle>
                </NoItemContainer>
              ) : (
                <InfiniteList
                  data={adminBusinesses}
                  height={100}
                  renderItem={(b) => (
                    <AdminBusinesses
                      business={b}
                      height={100}
                      onClick={() => alertCancellingEdition(() => setSelectedBusinessId(b.id))}
                    />
                  )}
                />
              )}
            </BusinessList>
          )}
        </LeftContainer>
      }
      content={
        <RightContainer>
          {!!selectedBusiness ? (
            <FormContainer>
              <SectionTitleContainer>{i18n.t('components.adminBusinesses.section01')}</SectionTitleContainer>
              <ColumnContainer>
                <RowContainer>
                  <FormItemContainer>
                    <TextInput
                      title={i18n.t('components.adminBusinesses.item01')}
                      value={selectedBusiness.title}
                      onChange={(title) => setFormField(() => setSelectedBusiness({ ...selectedBusiness, title }))}
                      placeholder={i18n.t('components.adminBusinesses.placeholder01')}
                      error={formErrors && formErrors.title}
                      nonEditable
                    />
                  </FormItemContainer>
                  <FormItemContainer>
                    <TextInput
                      title={i18n.t('components.adminBusinesses.item02')}
                      value={selectedBusiness.subtitle || ''}
                      onChange={(subtitle) =>
                        setFormField(() => setSelectedBusiness({ ...selectedBusiness, subtitle }))
                      }
                      placeholder={i18n.t('components.adminBusinesses.placeholder02')}
                      error={formErrors && formErrors.subtitle}
                      nonEditable
                    />
                  </FormItemContainer>
                </RowContainer>
                <RowContainer>
                  <FormItemContainer>
                    <TextInput
                      title={i18n.t('components.adminBusinesses.item03')}
                      value={selectedBusiness.address}
                      onChange={(address) => setFormField(() => setSelectedBusiness({ ...selectedBusiness, address }))}
                      placeholder={i18n.t('components.adminBusinesses.placeholder03')}
                      error={formErrors && formErrors.address}
                      nonEditable
                    />
                  </FormItemContainer>
                </RowContainer>
                <SectionTitleContainer>{i18n.t('components.adminBusinesses.section02')}</SectionTitleContainer>
                <RowContainer>
                  <FormItemContainer>
                    <SelectorContainer>
                      <SelectorTitle>{i18n.t('components.adminBusinesses.item04')}</SelectorTitle>
                      <MultiSelect
                        options={companyList}
                        selected={selectedBusiness.companies || []}
                        setSelected={(selected) =>
                          setFormField(() => setSelectedBusiness({ ...selectedBusiness, companies: selected || [] }))
                        }
                        placeholder={i18n.t('components.adminBusinesses.placeholder04')}
                        noMargin
                      />
                    </SelectorContainer>
                  </FormItemContainer>
                </RowContainer>
              </ColumnContainer>
              <FormButtonContainer>
                <Button
                  title={i18n.t('components.adminBusinesses.cancel')}
                  status={dirtyForm ? 'active' : 'disabled'}
                  type="classic"
                  size="medium"
                  onClick={() => alertCancellingEdition()}
                  icon="bin"
                />
                <Button
                  title={i18n.t('components.adminBusinesses.saveItem')}
                  status={dirtyForm && selectedBusiness && valideBusiness ? 'active' : 'disabled'}
                  type="navigation"
                  size="medium"
                  onClick={() => selectedBusiness && saveBusiness(selectedBusiness)}
                  icon="add"
                />
              </FormButtonContainer>
            </FormContainer>
          ) : (
            <NoSelectedContainer>
              <NoSelectedLibelle>{i18n.t('components.adminBusinesses.noSelected')}</NoSelectedLibelle>
            </NoSelectedContainer>
          )}
        </RightContainer>
      }
    />
  )
}

export default adminBusinesses

const LeftContainer = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px 10px 0 10px;
  overflow: hidden;
`
const StatusContainer = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 100%;
`
const ErrorLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
  margin: 40px;
`
const Back = styled('div')`
  ${(props) => props.theme.fonts.backLibelle};
  cursor: pointer;
`
const ScreenTitle = styled('div')`
  ${(props) => props.theme.fonts.title};
`
const ScreenTitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 10px;
  margin: 0 10px 15px 10px;
`
const BusinessList = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: scroll;
  /* padding-bottom: 75px; */
`
const NoItemContainer = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`
const NoItemLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
`
const RightContainer = styled('div')`
  flex: 1;
  display: flex;
  background-color: ${(props) => props.theme.colors.filterBackgroundColor};
`
const FormContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin: 50px;
  justify-content: flex-start;
  overflow: scroll;
`
const NoSelectedContainer = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`
const NoSelectedLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
`
const SectionTitleContainer = styled('div')`
  font-family: Avenir;
  font-style: normal;
  font-weight: 900;
  font-size: 20px;
  line-height: 24px;
  color: #0053a1;
  margin: 10px;
  margin-top: 0;
`
const RowContainer = styled('div')`
  display: flex;
  margin: 10px;
  margin-top: 0;
`
const ColumnContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin: 10px;
  margin-top: 0;
`
const FormItemContainer = styled('div')`
  display: flex;
  flex: 1;
  margin: 10px;
  margin-top: 0;
`
const FormButtonContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-around;
  position: absolute;
  bottom: 25px;
  right: 25px;
  min-width: 600px;
`
const SelectorContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;

  margin: 5px;
`
const SelectorTitle = styled('div')`
  font-family: Avenir;
  font-style: normal;
  font-weight: 900;
  font-size: 14px;
  line-height: 19px;
  color: #9494a2;
`
const SelectContainer = styled('div')`
  padding: 10px 0;
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  border-top-width: 1px;
`
